import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip, 
  Avatar, 
  IconButton, 
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
  Card,
  CardContent,
  CardActions,
  Stack
} from '@mui/material';
import {
  Description as DocumentIcon,
  Person as PersonIcon,
  PriorityHigh as PriorityIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

import LocalizedDate from '../../../components/common/LocalizedDate';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useFetchObjects from '../../../api/useFetchObjects';

// Styled components for table design
const StyledTableContainer = styled(TableContainer)(({ theme, isRtl }) => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  direction: isRtl ? 'rtl' : 'ltr',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    textAlign: isRtl ? 'right' : 'left',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.primary.main,
    textAlign: isRtl ? 'right' : 'left',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  height: 20,
  fontSize: '0.65rem',
  fontWeight: 600,
  borderRadius: 10,
  '& .MuiChip-label': {
    padding: '0 6px',
  }
}));

const DocumentTypeChip = styled(Chip)(({ theme }) => ({
  height: 20,
  fontSize: '0.65rem',
  fontWeight: 600,
  borderRadius: 10,
  '& .MuiChip-label': {
    padding: '0 6px',
  }
}));

const RecentDocumentsList = ({ documents = [] }) => {
  const { t, i18n } = useTranslation(['documents', 'common', 'tasks']);
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getPriorityColor = (priority) => {
    const colors = {
      HIGH: 'error',
      MEDIUM: 'warning',
      LOW: 'success',
      URGENT: 'error',
      عاجل: 'error',
      اطمینانیه: 'warning',
      عادي: 'success'
    };
    return colors[priority] || 'default';
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      مکتوب: 'primary',
      پیشنهاد: 'success',
      استعلام: 'info',
      گزارش: 'warning',
      LETTER: 'primary',
      PROPOSAL: 'success',
      INQUIRY: 'info',
      REPORT: 'warning'
    };
    return colors[type] || 'default';
  };

  // Helper to get owner display name
  const getOwnerName = (doc) => {
    return doc.creator?.full_name || doc.creator?.username ||
      doc.owner?.full_name || doc.owner?.username ||
      doc.owner_username || doc.created_by || doc.owner || t('common:unknown');
  };

  // Fetch task assignments for documents
  const documentIds = documents.map(doc => doc.id).join(',') || '';
  const { data: taskAssignmentsData, isLoading: taskAssignmentsLoading } = useFetchObjects(
    'task-assignments/by-document-ids',
    { document_ids: documentIds },
    user?.token,
    { enabled: Boolean(documentIds) }
  );

  // Helper to get latest status for a document
  const getDocumentTaskStatus = (docId) => {
    if (!taskAssignmentsData || !Array.isArray(taskAssignmentsData)) {
      return null;
    }
    const assignment = taskAssignmentsData.find(t => t.document_id === docId);
    return assignment?.status || null;
  };

  // Helper to get status color
  const getStatusColor = (status) => ({
    ASSIGNED: 'default',
    ACCEPTED: 'info',
    RECEIVED: 'info',
    IN_PROGRESS: 'primary',
    COMPLETED: 'success',
    REJECTED: 'error',
    REJECTED_RECEIPT: 'error',
    FORWARDED: 'warning',
    SKIPPED: 'secondary'
  }[status] || 'default');

  // Helper to get status label
  const getStatusLabel = (status) => {
    const statusDefaultsPs = {
      ASSIGNED: 'استول شوی',
      IN_PROGRESS: 'په جریان کی',
      COMPLETED: 'بشپړ شوی',
      REJECTED: 'رد شوی',
      REJECTED_RECEIPT: 'رد شوی',
      FORWARDED: 'راجع شوی',
      ACCEPTED: 'استول شوی',
      RECEIVED: 'استول شوی'
    };
    
    // Use the status directly as the translation key
    const key = `tasks:${status}`;
    const translated = t(key, statusDefaultsPs[status] || status);
    
    // Ensure we always return a string
    if (typeof translated === 'object') {
      console.error('Translation returned object instead of string:', translated);
      return statusDefaultsPs[status] || status;
    }
    
    return translated || statusDefaultsPs[status] || status;
  };

  // Filter documents based on user role
  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN');
  const isMyDocument = (doc) => {
    const username = user?.username;
    const userId = user?.id;
    return [doc.owner, doc.owner_username, doc.created_by, doc.creator?.username]
      .some((val) => val && val === username) || (doc.creator?.id && doc.creator.id === userId);
  };

  // Sort by created date desc, pick latest 10 for table layout
  const mySortedDocs = (documents || [])
    .filter((doc) => isSuperAdmin ? true : isMyDocument(doc))
    .sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0))
    .slice(0, 10);

  const filteredDocuments = mySortedDocs;

  const handleDocumentClick = (docId) => {
    navigate(`/documents/${docId}`);
  };

  // Mobile Card Component
  const DocumentCard = ({ doc }) => (
    <Card sx={{ 
      mb: 2, 
      borderRadius: 2, 
      boxShadow: 2,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: 4,
        transform: 'translateY(-2px)'
      }
    }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Header with Document Info */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 1.5,
          mb: 2,
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <Avatar sx={{ 
            width: 40, 
            height: 40, 
            bgcolor: `${getDocumentTypeColor(doc.type)}.main`,
            fontSize: '0.9rem',
            fontWeight: 600,
            flexShrink: 0
          }}>
            {doc.type?.charAt(0) || 'D'}
          </Avatar>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5,
                textAlign: isRtl ? 'right' : 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {doc.title || t('documents:untitled')}
            </Typography>
            {doc.reference_number && (
              <Typography 
                variant="caption" 
                color="primary.main" 
                fontWeight={600}
                sx={{ textAlign: isRtl ? 'right' : 'left' }}
              >
                #{doc.reference_number}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Document Details */}
        <Stack spacing={1.5}>
          {/* Document Type and Priority Row */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('documents:type')}
              </Typography>
              <DocumentTypeChip 
                label={doc.type || 'Unknown'} 
                size="small" 
                color={getDocumentTypeColor(doc.type)}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('documents:priority')}
              </Typography>
              <StatusChip 
                label={doc.priority || 'Normal'} 
                size="small" 
                color={getPriorityColor(doc.priority)}
              />
            </Box>
          </Box>

          {/* Owner and Pages Row */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('documents:owner')}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '120px'
                  }}
                >
                  {getOwnerName(doc)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('documents:number_of_pages')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                {doc.number_of_pages != null ? doc.number_of_pages : '-'}
              </Typography>
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('common:status')}
            </Typography>
            {taskAssignmentsLoading ? (
              <Skeleton variant="rectangular" width={80} height={20} />
            ) : (
              (() => {
                const status = getDocumentTaskStatus(doc.id);
                if (!status) return (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    -
                  </Typography>
                );
                
                const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
                if (!validTaskStatuses.includes(status)) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      -
                    </Typography>
                  );
                }
                
                const statusLabel = getStatusLabel(status);
                const finalLabel = typeof statusLabel === 'string' ? statusLabel : status;
                
                return (
                  <StatusChip 
                    label={finalLabel} 
                    size="small" 
                    color={getStatusColor(status)}
                  />
                );
              })()
            )}
          </Box>
        </Stack>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          width: '100%', 
          justifyContent: isRtl ? 'flex-start' : 'flex-end' 
        }}>
          <Tooltip title={t('common:view', 'View')}>
            <IconButton 
              size="small"
              sx={{ 
                backgroundColor: 'primary.50',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.100'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentClick(doc.id);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );

  if (!filteredDocuments.length) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        py: 6,
        textAlign: 'center'
      }}>
        <Avatar sx={{ 
          width: 64, 
          height: 64, 
          bgcolor: 'grey.100', 
          mb: 2 
        }}>
          <DocumentIcon sx={{ fontSize: 32, color: 'grey.400' }} />
        </Avatar>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('documents:no_recent_docs', 'No Recent Documents')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('documents:no_docs_description', 'You haven\'t created any documents yet')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        // Mobile Card View
        <Box>
          {filteredDocuments.map((doc) => (
            <DocumentCard key={doc.id} doc={doc} />
          ))}
        </Box>
      ) : (
        // Desktop Table View
        <StyledTableContainer component={Paper} elevation={0} isRtl={isRtl}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                  {isRtl ? t('documents:document', 'Document') : t('documents:document', 'Document')}
                </TableCell>
                <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                  {isRtl ? t('documents:type', 'Type') : t('documents:type', 'Type')}
                </TableCell>
                <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                  {isRtl ? t('documents:priority', 'Priority') : t('documents:priority', 'Priority')}
                </TableCell>
                <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                  {isRtl ? t('documents:owner', 'Owner') : t('documents:owner', 'Owner')}
                </TableCell>
                <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                  {isRtl ? t('documents:number_of_pages', 'Pages') : t('documents:number_of_pages', 'Pages')}
                </TableCell>
                <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                  {isRtl ? t('common:status', 'Status') : t('common:status', 'Status')}
                </TableCell>
                <TableCell align="center">
                  {isRtl ? t('common:actions', 'Actions') : t('common:actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow 
                  key={doc.id} 
                  onClick={() => handleDocumentClick(doc.id)}
                  sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                >
                  {/* Document Info */}
                  <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      flexDirection: isRtl ? 'row-reverse' : 'row',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start'
                    }}>
                      <Avatar sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: `${getDocumentTypeColor(doc.type)}.main`,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        order: isRtl ? 1 : 0
                      }}>
                        {doc.type?.charAt(0) || 'D'}
                      </Avatar>
                      <Box sx={{ 
                        minWidth: 0, 
                        flexGrow: 1,
                        textAlign: isRtl ? 'right' : 'left'
                      }}>
                        <Typography 
                          variant="body2" 
                          fontWeight={600}
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                            textAlign: isRtl ? 'right' : 'left'
                          }}
                        >
                          {doc.title || t('documents:untitled')}
                        </Typography>
                        {doc.reference_number && (
                          <Typography 
                            variant="caption" 
                            color="primary.main" 
                            fontWeight={600}
                            sx={{ textAlign: isRtl ? 'right' : 'left' }}
                          >
                            #{doc.reference_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Document Type */}
                  <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <DocumentTypeChip 
                      label={doc.type || 'Unknown'} 
                      size="small" 
                      color={getDocumentTypeColor(doc.type)}
                    />
                  </TableCell>

                  {/* Priority */}
                  <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <StatusChip 
                      label={doc.priority || 'Normal'} 
                      size="small" 
                      color={getPriorityColor(doc.priority)}
                    />
                  </TableCell>

                  {/* Owner */}
                  <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      flexDirection: isRtl ? 'row-reverse' : 'row',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start'
                    }}>
                      <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        noWrap
                        sx={{ textAlign: isRtl ? 'right' : 'left' }}
                      >
                        {getOwnerName(doc)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Pages */}
                  <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>
                      {doc.number_of_pages != null ? doc.number_of_pages : '-'}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    {taskAssignmentsLoading ? (
                      <Skeleton variant="rectangular" width={60} height={20} />
                    ) : (
                      (() => {
                        const status = getDocumentTaskStatus(doc.id);
                        console.log('Document ID:', doc.id, 'Status:', status, 'Document status:', doc.status);
                        if (!status) return '-';
                        
                        // Only use task assignment statuses, not document statuses
                        const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
                        if (!validTaskStatuses.includes(status)) {
                          console.warn('Invalid task status:', status);
                          return '-';
                        }
                        
                        const statusLabel = getStatusLabel(status);
                        console.log('Status label:', statusLabel, 'Type:', typeof statusLabel);
                        
                        // Ensure we have a string label
                        const finalLabel = typeof statusLabel === 'string' ? statusLabel : status;
                        
                        return (
                          <StatusChip 
                            label={finalLabel} 
                            size="small" 
                            color={getStatusColor(status)}
                          />
                        );
                      })()
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Tooltip title={t('common:view', 'View')}>
                      <IconButton 
                        size="small" 
                        sx={{ color: 'primary.main' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDocumentClick(doc.id);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}
    </Box>
  );
};

export default RecentDocumentsList;
